import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  fetchRoles,
  fetchPermissionCatalog,
  createRole,
  updateRole,
  deleteRole,
  type Role,
  type CreateRoleInput,
} from '@/api/roles.api';
import { QUERY_KEYS } from '@/api/queryKeys';
import { useConfirm } from '@/components/common/ConfirmDialog';
import type { PermissionGroupDefinition } from '@genealogy-app/shared';

// ─── Modal-Helfer ───────────────────────────────────────────────────────────

function ModalOverlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-xl sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Rolle-Modal ────────────────────────────────────────────────────────────

interface RoleModalProps {
  role: Role | null;
  groups: PermissionGroupDefinition[];
  onClose: () => void;
}

function RoleModal({ role, groups, onClose }: RoleModalProps) {
  const queryClient = useQueryClient();
  const isEdit = role !== null;
  const isReadOnly = role?.isSystem ?? false;

  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [permissions, setPermissions] = useState<Set<string>>(
    new Set(role?.permissions ?? []),
  );

  const togglePermission = (perm: string) => {
    if (isReadOnly) return;
    setPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(perm)) next.delete(perm);
      else next.add(perm);
      return next;
    });
  };

  const toggleGroup = (group: PermissionGroupDefinition, enable: boolean) => {
    if (isReadOnly) return;
    setPermissions((prev) => {
      const next = new Set(prev);
      const keys = [group.view, ...(group.manage ? [group.manage] : [])];
      for (const k of keys) {
        if (enable) next.add(k);
        else next.delete(k);
      }
      return next;
    });
  };

  const createMutation = useMutation({
    mutationFn: (input: CreateRoleInput) => createRole(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users.all });
      toast.success('Rolle wurde angelegt.');
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Anlegen fehlgeschlagen.'),
  });

  const updateMutation = useMutation({
    mutationFn: (input: CreateRoleInput) => updateRole(role!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users.all });
      toast.success('Rolle aktualisiert.');
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Aktualisieren fehlgeschlagen.'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isReadOnly) return;
    const input: CreateRoleInput = {
      name: name.trim(),
      description: description.trim() || null,
      permissions: Array.from(permissions),
    };
    if (isEdit) updateMutation.mutate(input);
    else createMutation.mutate(input);
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <ModalOverlay onClose={onClose}>
      <h2 className="mb-4 text-lg font-semibold text-slate-900">
        {isEdit ? (isReadOnly ? `Rolle: ${role?.name}` : 'Rolle bearbeiten') : 'Neue Rolle'}
      </h2>

      {isReadOnly && (
        <div className="mb-4 rounded-md bg-purple-50 p-3 text-sm text-purple-700">
          Diese Systemrolle kann nicht bearbeitet werden. Admins haben automatisch
          Zugriff auf alle Funktionen (Superuser-Bypass).
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="roleName" className="mb-1 block text-sm font-medium text-slate-700">
            Name
          </label>
          <input
            id="roleName"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isReadOnly}
            required
            maxLength={100}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
          />
        </div>

        <div>
          <label htmlFor="roleDesc" className="mb-1 block text-sm font-medium text-slate-700">
            Beschreibung (optional)
          </label>
          <textarea
            id="roleDesc"
            value={description ?? ''}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isReadOnly}
            rows={2}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
          />
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-slate-700">Berechtigungen</p>
          <div className="space-y-3">
            {groups.map((group) => {
              const allKeys = [group.view, ...(group.manage ? [group.manage] : [])];
              const checkedCount = allKeys.filter((k) => permissions.has(k)).length;
              const allChecked = checkedCount === allKeys.length;
              const someChecked = checkedCount > 0 && !allChecked;
              return (
                <div
                  key={group.key}
                  className="rounded-md border border-slate-200 bg-slate-50 p-3"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{group.label}</p>
                      {group.description && (
                        <p className="text-xs text-slate-500">{group.description}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleGroup(group, !allChecked)}
                      disabled={isReadOnly}
                      className="text-xs text-blue-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                    >
                      {allChecked ? 'Alle abwählen' : 'Alle aktivieren'}
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3">
                    <label className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={permissions.has(group.view)}
                        onChange={() => togglePermission(group.view)}
                        disabled={isReadOnly}
                        ref={(el) => {
                          if (el) el.indeterminate = someChecked && !permissions.has(group.view);
                        }}
                        className="rounded border-slate-300"
                      />
                      Ansehen
                    </label>
                    {group.manage && (
                      <label className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={permissions.has(group.manage)}
                          onChange={() => togglePermission(group.manage!)}
                          disabled={isReadOnly}
                          className="rounded border-slate-300"
                        />
                        Verwalten
                      </label>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            {isReadOnly ? 'Schließen' : 'Abbrechen'}
          </button>
          {!isReadOnly && (
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isPending ? 'Wird gespeichert...' : isEdit ? 'Speichern' : 'Anlegen'}
            </button>
          )}
        </div>
      </form>
    </ModalOverlay>
  );
}

// ─── AdminRolesPage ─────────────────────────────────────────────────────────

export function AdminRolesPage() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [editRole, setEditRole] = useState<Role | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data: roles, isLoading } = useQuery({
    queryKey: QUERY_KEYS.roles.all,
    queryFn: fetchRoles,
  });

  const { data: catalog } = useQuery({
    queryKey: QUERY_KEYS.roles.permissions,
    queryFn: fetchPermissionCatalog,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.roles.all });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users.all });
      toast.success('Rolle gelöscht.');
    },
    onError: (err: Error) => toast.error(err.message || 'Löschen fehlgeschlagen.'),
  });

  const groups = useMemo(() => catalog?.groups ?? [], [catalog]);

  const handleDelete = async (role: Role) => {
    if (role.isSystem) {
      toast.error('Systemrollen können nicht gelöscht werden.');
      return;
    }
    const ok = await confirm({
      title: 'Rolle löschen?',
      message: (
        <>
          Rolle <strong>{role.name}</strong> wirklich löschen?
          {role.userCount ? ` ${role.userCount} Nutzer verlieren ihre Berechtigungen.` : ''}
        </>
      ),
      confirmLabel: 'Löschen',
      variant: 'danger',
    });
    if (!ok) return;
    deleteMutation.mutate(role.id);
  };

  return (
    <div className="flex h-full flex-col bg-slate-50">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm">
        <div className="flex items-center gap-2 sm:gap-4">
          <h1 className="text-base font-semibold text-slate-800 sm:text-lg">Rollenverwaltung</h1>
        </div>

        <button
          onClick={() => setShowCreate(true)}
          disabled={!catalog}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          <span className="hidden sm:inline">Rolle anlegen</span>
          <span className="sm:hidden">Anlegen</span>
        </button>
      </header>

      <main className="flex-1 overflow-y-auto p-4 sm:p-8">
        <div className="mx-auto w-full max-w-4xl">
          {isLoading && (
            <div className="flex justify-center py-12">
              <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
            </div>
          )}

          {roles && roles.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
              <p className="text-sm text-slate-500">
                Noch keine Rollen vorhanden. Lege eine Rolle an, um Nutzern Zugriff zu gewähren.
              </p>
            </div>
          )}

          {roles && roles.length > 0 && (
            <div className="space-y-3">
              {roles.map((role) => (
                <div
                  key={role.id}
                  className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                >
                  {/* Am Handy untereinander: neben zwei Knöpfen bleiben für den
                      Beschreibungstext sonst nur ~120 px, und ein Satz zerfällt
                      in sechs Zeilen. */}
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold text-slate-900">{role.name}</h3>
                        {role.isSystem && (
                          <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
                            System
                          </span>
                        )}
                        {typeof role.userCount === 'number' && (
                          <span className="text-xs text-slate-500">
                            {role.userCount} Nutzer
                          </span>
                        )}
                      </div>
                      {role.description && (
                        <p className="mt-1 text-sm text-slate-500">{role.description}</p>
                      )}
                      <p className="mt-2 text-xs text-slate-400">
                        {role.permissions.length} Berechtigungen
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button
                        onClick={() => setEditRole(role)}
                        className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        {role.isSystem ? 'Anzeigen' : 'Bearbeiten'}
                      </button>
                      {!role.isSystem && (
                        <button
                          onClick={() => handleDelete(role)}
                          className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                        >
                          Löschen
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {showCreate && catalog && (
        <RoleModal role={null} groups={groups} onClose={() => setShowCreate(false)} />
      )}
      {editRole && catalog && (
        <RoleModal role={editRole} groups={groups} onClose={() => setEditRole(null)} />
      )}
    </div>
  );
}
